import React, { useState, useRef, useEffect } from 'react';
import { Editor, Toolbar } from '@wangeditor/editor-for-react';
import '@wangeditor/editor/dist/css/style.css';

function WangEditor({ value, onChange }) {
  const [editor, setEditor] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const editorRef = useRef(null);
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const IMAGES_API_BASE_URL = process.env.REACT_APP_images_API_BASE_URL;

  // 配置图片上传接口
  const defaultConfig = {
    placeholder: '请输入内容...',
    MENU_CONF: {
      uploadImage: {
        server: `${API_BASE_URL}/forms/upload`,
        fieldName: 'file',
        maxFileSize: 10 * 1024 * 1024, // 10MB，新增限制
        customInsert(res, insertFn) {
          let url = res.url;
          if (url && url.startsWith('/uploads/')) {
            // 保证只有一个斜杠
            url = IMAGES_API_BASE_URL.replace(/\/$/, '') + url;
          }
          insertFn(url, '', url);
        }
      },
      // 配置字号菜单
      fontSize: {
        fontSizeList: ['12px', '13px', '14px', '15px', '16px', '19px', '22px', '24px', '32px', '48px']
      }
    }
  };

  // 工具栏配置，字号放在最前面
  const toolbarConfig = {
    toolbarKeys: [
      'fontSize', 'headerSelect', 'bold', 'underline', 'italic', 'color', 'bgColor',
      'bulletedList', 'numberedList', 'todo', 'insertLink', 'insertImage', 'insertTable',
      'emotion', 'code', 'blockquote', 'divider', 'undo', 'redo', 'clearStyle',
      // 你可以根据需要添加更多菜单
    ],
    // 尝试通过其他方式配置字号
    excludeKeys: []
  };

  // 悬浮快捷工具栏配置
  const editorConfig = {
    placeholder: '请输入内容...',
    MENU_CONF: defaultConfig.MENU_CONF,
    // 简化悬浮工具栏配置，专注于基本功能
    hoverbarKeys: {
      text: ['bold', 'italic', 'underline', 'color', 'bgColor', 'insertLink', 'bulletedList', 'numberedList', 'clearStyle'],
      link: ['unLink'],
      image: ['deleteImage'],
      table: ['deleteTable']
    }
  };

  // 内容高度自适应（失焦后收缩高度）
  useEffect(() => {
    if (!editor) return;
    const updateHeight = () => {
      const elem = editorRef.current?.querySelector('.w-e-text-container');
      if (elem) {
        if (isExpanded) {
          // 展开状态：自适应高度
          elem.style.height = 'auto';
          let h = elem.scrollHeight;
          h = Math.max(200, h); // 最小高度200px
          elem.style.height = h + 'px';
          elem.style.overflowY = 'hidden';
        } else {
          // 收缩状态：固定小高度
          elem.style.height = '120px';
          elem.style.overflowY = 'auto';
        }
      }
    };
    updateHeight();
    if (isExpanded) {
      editor.on('change', updateHeight);
    }
    return () => {
      editor.off('change', updateHeight);
    };
  }, [editor, value, isExpanded]);

  // 注入图片自适应样式和字号工具栏样式
  useEffect(() => {
    const styleId = 'wangeditor-img-autofit';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.innerHTML = `
        .w-e-text-container img { max-width: 100%; height: auto !important; display: block; margin: 0 auto; }
        /* 让字号设置更显眼 */
        .w-e-toolbar .w-e-menu[data-menu-key="fontSize"] {
          background-color: #f0f8ff !important;
          border: 1px solid #1890ff !important;
          border-radius: 4px !important;
        }
        .w-e-toolbar .w-e-menu[data-menu-key="fontSize"]:hover {
          background-color: #e6f7ff !important;
        }
        /* 悬浮工具栏样式优化 */
        .w-e-hover-bar {
          box-shadow: 0 4px 12px rgba(0,0,0,0.15) !important;
          border-radius: 6px !important;
        }
        /* 固定工具栏样式 */
        .w-e-toolbar {
          position: sticky !important;
          top: 0 !important;
          z-index: 10 !important;
          background-color: #fff !important;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1) !important;
        }
        /* 收缩状态下的编辑器样式 */
        .w-e-text-container {
          transition: height 0.3s ease !important;
        }
        /* 收缩状态下的占位符样式 */
        .w-e-text-placeholder {
          color: #999 !important;
          font-style: italic !important;
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  // 处理点击事件
  const handleClick = () => {
    setIsExpanded(true);
  };

  // 添加全局点击事件监听
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (editorRef.current && !editorRef.current.contains(event.target)) {
        setIsExpanded(false);
      }
    };

    if (isExpanded) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isExpanded]);

  useEffect(() => {
    return () => {
      if (editor) editor.destroy();
    };
  }, [editor]);

  return (
    <div 
      style={{ border: '1px solid #ccc', borderRadius: 8, position: 'relative' }} 
      ref={editorRef}
      onClick={handleClick}
    >
      <Toolbar 
        editor={editor} 
        defaultConfig={toolbarConfig} 
        mode="default" 
        style={{ 
          borderBottom: '1px solid #ccc',
          position: 'sticky',
          top: 0,
          zIndex: 10,
          backgroundColor: '#fff',
          borderRadius: '8px 8px 0 0'
        }} 
      />
      <Editor
        defaultConfig={defaultConfig}
        editorConfig={editorConfig}
        value={value}
        onCreated={setEditor}
        onChange={editor => onChange(editor.getHtml())}
        mode="default"
        style={{ 
          minHeight: isExpanded ? 200 : 120, 
          padding: 8,
          transition: 'min-height 0.3s ease'
        }}
      />
    </div>
  );
}

export default WangEditor; 